import React, { useState, useEffect } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { AdminSidebar } from './AdminSidebar';
import { AdminHeader } from './AdminHeader';
import { AdminFooter } from './AdminFooter';
import { cn } from '@/lib/utils';

export const AdminLayout: React.FC = () => {
  const { isAuthenticated } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Initialize based on screen size, but only on first load
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return true;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Handle responsive sidebar - only set initial state, don't override user preference
  useEffect(() => {
    if (!isInitialized) {
      const handleInitialResize = () => {
        if (window.innerWidth < 1024) {
          setSidebarOpen(false);
        } else {
          setSidebarOpen(true);
        }
        setIsInitialized(true);
      };

      handleInitialResize();
      window.addEventListener('resize', handleInitialResize);
      return () => window.removeEventListener('resize', handleInitialResize);
    }
  }, [isInitialized]);

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Calculate main content margin based on sidebar state
  const getMainMargin = () => {
    if (!sidebarOpen) {
      return 'ml-0';
    }
    return sidebarCollapsed ? 'ml-16' : 'ml-64';
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AdminHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex flex-1 relative">
        <AdminSidebar 
          isOpen={sidebarOpen} 
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          onCollapseChange={setSidebarCollapsed}
        />
        <main 
          id="main-content"
          className={cn(
            "flex-1 transition-all duration-300 ease-in-out",
            getMainMargin()
          )}
          role="main"
          aria-label="Main content"
        >
          <div className="container mx-auto px-4 py-6 min-h-[calc(100vh-4rem)]">
            <Outlet />
          </div>
        </main>
      </div>
      <AdminFooter />
    </div>
  );
};


